import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as DeliveredIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { Order, OrderStatus, PaymentStatus, DeliveryPerson, OrderStatusHistory, DeliveryPersonStatus } from '../../types/models';
import { orderService, deliveryPersonService } from '../../services/firebase';
import { getOrderStatusColor } from '../../theme/theme';
import { useAuditLog } from '../../hooks/useAuditLog';
import { AuditAction } from '../../types/audit';
import { logger } from '../../services/loggerService';

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [deliveryAgents, setDeliveryAgents] = useState<DeliveryPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus>(OrderStatus.PENDING);
  const [statusNotes, setStatusNotes] = useState('');
  const [selectedDeliveryAgent, setSelectedDeliveryAgent] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const { logOrderAction } = useAuditLog();

  const loadOrders = async () => {
    setLoading(true);
    try {
      logger.debug('Loading orders and delivery agents');
      
      const [ordersData, deliveryAgentsData] = await Promise.all([
        orderService.getOrders(),
        deliveryPersonService.getDeliveryPersons()
      ]);
      
      setOrders(ordersData);
      setDeliveryAgents(deliveryAgentsData);
      logger.info('Orders and delivery agents loaded', { 
        orderCount: ordersData.length, 
        deliveryAgentCount: deliveryAgentsData.length 
      });
    } catch (error) {
      logger.error('Error loading orders data', error, { component: 'Orders', action: 'loadOrders' });
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleUpdateStatus = (order: Order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusNotes('');
    const agentId = order.deliveryAgent?.id || order.assignedDeliveryAgent || '';
    setSelectedDeliveryAgent(agentId);
    setStatusUpdateOpen(true);
  };

  const requiresDeliveryAgent = (status: OrderStatus): boolean => {
    return status === OrderStatus.DELIVERY_ASSIGNED || status === OrderStatus.OUT_FOR_DELIVERY;
  };

  const getValidNextStatuses = (currentStatus: OrderStatus): OrderStatus[] => {
    const statusOrder = [
      OrderStatus.PENDING,
      OrderStatus.CONFIRMED,
      OrderStatus.DELIVERY_ASSIGNED,
      OrderStatus.OUT_FOR_DELIVERY,
      OrderStatus.DELIVERED
    ];

    const currentIndex = statusOrder.indexOf(currentStatus);
    
    const validStatuses = statusOrder.slice(currentIndex);
    
    if (currentStatus !== OrderStatus.DELIVERED && 
        currentStatus !== OrderStatus.CANCELLED && 
        currentStatus !== OrderStatus.RETURNED && 
        currentStatus !== OrderStatus.REFUNDED) {
      validStatuses.push(OrderStatus.CANCELLED);
    }
    
    if (currentStatus === OrderStatus.DELIVERED) {
      validStatuses.push(OrderStatus.RETURNED);
    }
    
    if (currentStatus === OrderStatus.CANCELLED || currentStatus === OrderStatus.RETURNED) {
      validStatuses.push(OrderStatus.REFUNDED);
    }
    
    return validStatuses;
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder) return;

    const validStatuses = getValidNextStatuses(selectedOrder.status);
    if (!validStatuses.includes(newStatus)) {
      setSnackbar({ 
        open: true, 
        message: 'Invalid status transition. Cannot move backwards in the order process.', 
        severity: 'error' 
      });
      return;
    }

    if (requiresDeliveryAgent(newStatus) && !selectedDeliveryAgent) {
      setSnackbar({ 
        open: true, 
        message: 'Please select a delivery agent for this status', 
        severity: 'error' 
      });
      return;
    }

    try {
      logger.debug('Updating order status', { 
        orderId: selectedOrder.id, 
        oldStatus: selectedOrder.status,
        newStatus, 
        statusNotes,
        deliveryAgent: selectedDeliveryAgent || 'none'
      });
      
      let deliveryAgentObject: any = undefined;
      if (selectedDeliveryAgent) {
        const agent = getDeliveryAgentInfo(selectedDeliveryAgent);
        if (agent) {
          deliveryAgentObject = {
            id: agent.id,
            name: agent.name,
            phone: agent.phone,
            email: agent.email,
            vehicleType: agent.vehicleType,
            vehicleNumber: agent.vehicleNumber
          };
          
          if (agent.profileImage) {
            deliveryAgentObject.profileImage = agent.profileImage;
          }
        }
      }

      await orderService.updateOrderStatus(
        selectedOrder.id, 
        newStatus, 
        statusNotes, 
        'admin', // TODO: Get actual user ID from auth context
        selectedDeliveryAgent || undefined,
        deliveryAgentObject
      );
      
      await logOrderAction(
        AuditAction.ORDER_STATUS_UPDATE,
        selectedOrder.id,
        { 
          before: { status: selectedOrder.status },
          after: { status: newStatus, deliveryAgent: deliveryAgentObject }
        },
        true,
        undefined,
        {
          notes: statusNotes,
          deliveryAgentAssigned: selectedDeliveryAgent ? true : false
        }
      );
      
      logger.info('Order status updated successfully', { orderId: selectedOrder.id, newStatus });
      
      const updatedOrder = { 
        ...selectedOrder, 
        status: newStatus,
        updatedAt: new Date(),
        assignedDeliveryAgent: selectedDeliveryAgent || selectedOrder.assignedDeliveryAgent,
        deliveryAgent: deliveryAgentObject || selectedOrder.deliveryAgent
      };
      setOrders(prev => prev.map(order => order.id === selectedOrder.id ? updatedOrder : order));
      
      let message = 'Order status updated successfully';
      if (selectedDeliveryAgent) {
        const agent = getDeliveryAgentInfo(selectedDeliveryAgent);
        message += ` and assigned to ${agent?.name}`;
      }
      
      setSnackbar({ open: true, message, severity: 'success' });
      setStatusUpdateOpen(false);
    } catch (error: any) {
      console.error('❌ Error updating order status:', error);
      setSnackbar({ open: true, message: `Error updating order status: ${error?.message || 'Unknown error'}`, severity: 'error' });
    }
  };

  const getStatusText = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'Pending';
      case OrderStatus.CONFIRMED:
        return 'Confirmed';
      case OrderStatus.DELIVERY_ASSIGNED:
        return 'Delivery Assigned';
      case OrderStatus.OUT_FOR_DELIVERY:
        return 'Out for Delivery';
      case OrderStatus.DELIVERED:
        return 'Delivered';
      case OrderStatus.CANCELLED:
        return 'Cancelled';
      case OrderStatus.RETURNED:
        return 'Returned';
      case OrderStatus.REFUNDED:
        return 'Refunded';
      default:
        return String(status).replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase());
    }
  };

  const getDeliveryAgentInfo = (agentId: string | undefined) => {
    if (!agentId) return null;
    return deliveryAgents.find(a => a.id === agentId);
  };

  const getOrderDeliveryAgent = (order: Order) => {
    if (order.deliveryAgent) {
      return order.deliveryAgent;
    }
    if (order.assignedDeliveryAgent) {
      return getDeliveryAgentInfo(order.assignedDeliveryAgent);
    }
    return null;
  };

  const getPaymentStatusText = (status: PaymentStatus) => {
    return status.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const formatCurrency = (amount: number | undefined) => `₹${(amount || 0).toLocaleString()}`;

  const filteredOrders = orders.filter(order => {
    const customer = order.customer;
    const address = order.deliveryAddress;
    const matchesSearch = order.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer?.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.fullAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.pincode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         address?.addressLine1?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Orders
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage customer orders and track deliveries
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                placeholder="Search by order ID or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {Object.values(OrderStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {getStatusText(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                More Filters
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Delivery Agent</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        #{order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={order.customer?.profileImage || undefined}
                          sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}
                        >
                          {order.customer?.name?.charAt(0) || order.deliveryAddress?.title?.charAt(0) || order.deliveryAddress?.name?.charAt(0) || 'N'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {order.customer?.name || order.deliveryAddress?.title || order.deliveryAddress?.name || 'No Name'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.customer?.mobile || order.deliveryAddress?.phone || 'No Phone'}
                          </Typography>
                          {order.customer?.email && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {order.customer.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.items.length} items
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(order.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(order.status)}
                        size="small"
                        sx={{
                          bgcolor: `${getOrderStatusColor(order.status)}20`,
                          color: getOrderStatusColor(order.status),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const agent = getOrderDeliveryAgent(order);
                        return agent ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={agent.profileImage} 
                              sx={{ width: 24, height: 24, mr: 1, bgcolor: 'success.main', fontSize: '0.75rem' }}
                            >
                              {agent.name?.charAt(0) || 'D'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {agent.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {agent.vehicleType}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not assigned
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPaymentStatusText(order.paymentStatus)}
                        size="small"
                        color={order.paymentStatus === PaymentStatus.PAID ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : 'No Date'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.orderDate ? new Date(order.orderDate).toLocaleTimeString() : 'No Time'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleViewDetails(order)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleUpdateStatus(order)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Order Details - #{selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
                <Typography variant="h6" gutterBottom>Customer Information</Typography>
                <Typography><strong>Name:</strong> {selectedOrder.deliveryAddress?.name || selectedOrder.customer?.name || selectedOrder.deliveryAddress?.title || 'No Name'}</Typography>
                <Typography><strong>Phone:</strong> {selectedOrder.deliveryAddress?.phone || selectedOrder.customer?.mobile || 'No Phone'}</Typography>
                {selectedOrder.customer?.email && (
                  <Typography><strong>Email:</strong> {selectedOrder.customer.email}</Typography>
                )}
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Delivery Address</Typography>
                <Typography><strong>Title:</strong> {selectedOrder.deliveryAddress?.title || selectedOrder.deliveryAddress?.name || 'No Title'}</Typography>
                <Typography><strong>Address:</strong> {selectedOrder.deliveryAddress?.fullAddress || selectedOrder.deliveryAddress?.addressLine1 || 'No Address'}</Typography>
                {selectedOrder.deliveryAddress?.wingFlatNumber && (
                  <Typography><strong>Wing/Flat:</strong> {selectedOrder.deliveryAddress.wingFlatNumber}</Typography>
                )}
                {selectedOrder.deliveryAddress?.area && (
                  <Typography><strong>Area:</strong> {selectedOrder.deliveryAddress.area}</Typography>
                )}
                <Typography><strong>City:</strong> {selectedOrder.deliveryAddress?.city || 'No City'}</Typography>
                <Typography><strong>State:</strong> {selectedOrder.deliveryAddress?.state || 'No State'}</Typography>
                <Typography><strong>Pincode:</strong> {selectedOrder.deliveryAddress?.pincode || 'No Pincode'}</Typography>
                {selectedOrder.deliveryAddress?.latitude && selectedOrder.deliveryAddress?.longitude && (
                  <Typography><strong>Coordinates:</strong> {selectedOrder.deliveryAddress.latitude}, {selectedOrder.deliveryAddress.longitude}</Typography>
                )}
                {selectedOrder.deliveryAddress?.landmark && (
                  <Typography><strong>Landmark:</strong> {selectedOrder.deliveryAddress.landmark}</Typography>
                )}
              </Box>
              <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
                <Typography variant="h6" gutterBottom>Order Information</Typography>
                <Typography><strong>Order Date:</strong> {new Date(selectedOrder.orderDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                <Typography><strong>Delivery Date:</strong> {new Date(selectedOrder.deliveryDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</Typography>
                <Typography><strong>Status:</strong> {getStatusText(selectedOrder.status)}</Typography>
                <Typography><strong>Payment:</strong> {getPaymentStatusText(selectedOrder.paymentStatus)}</Typography>
                <Typography><strong>Delivery Slot:</strong> {selectedOrder.deliveryTimeSlot}</Typography>
                <Typography>
                  <strong>Delivery Agent:</strong> {
                    (() => {
                      const agent = getOrderDeliveryAgent(selectedOrder);
                      return agent ? `${agent.name} (${agent.vehicleType})` : (
                        <span style={{ fontStyle: 'italic', color: '#666' }}>Not assigned</span>
                      );
                    })()
                  }
                </Typography>
              </Box>
              
              <Box sx={{ width: '100%' }}>
                <Typography variant="h6" gutterBottom>Items</Typography>
                {selectedOrder.items.map((item, index) => {
                  const productName = (item as any).name || item.product?.name || item.productName || 'Unknown Product';
                  const unit = item.selectedWeight?.weight || item.unit || item.product?.unit || 'kg';
                  const itemPrice = item.totalPrice || (item.unitPrice || item.price || item.product?.price || 0) * item.quantity;
                  
                  return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {productName.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {productName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty: {item.quantity} {unit}
                        </Typography>
                        {item.product?.category && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Category: {item.product.category}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(itemPrice)}
                      </Typography>
                    </Box>
                  );
                })}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total: {formatCurrency(selectedOrder.totalAmount)}</Typography>
                </Box>
              </Box>

              {/* Status History Section */}
              {selectedOrder.statusHistory && selectedOrder.statusHistory.length > 0 && (
                <Box sx={{ width: '100%', mt: 3 }}>
                  <Typography variant="h6" gutterBottom>Status History</Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {selectedOrder.statusHistory
                      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                      .map((history, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          mb: 2, 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          borderRadius: 1,
                          borderLeft: 4,
                          borderLeftColor: getOrderStatusColor(history.status)
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip
                                label={getStatusText(history.status)}
                                size="small"
                                sx={{
                                  bgcolor: `${getOrderStatusColor(history.status)}20`,
                                  color: getOrderStatusColor(history.status),
                                  fontWeight: 600,
                                }}
                              />
                              {history.previousStatus && (
                                <Typography variant="caption" color="text.secondary">
                                  from {getStatusText(history.previousStatus)}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(history.timestamp).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Updated by: {history.updatedBy}
                            </Typography>
                            {history.notes && (
                              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                "{history.notes}"
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateOpen} onClose={() => setStatusUpdateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value as OrderStatus)}
            >
              {selectedOrder && getValidNextStatuses(selectedOrder.status).map((status) => (
                <MenuItem key={status} value={status}>
                  {getStatusText(status)}
                  {status === selectedOrder.status && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Current)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          {/* Delivery Agent Selection - Show when status requires delivery agent */}
          {requiresDeliveryAgent(newStatus) && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign Delivery Agent *</InputLabel>
              <Select
                value={selectedDeliveryAgent}
                label="Assign Delivery Agent *"
                onChange={(e) => setSelectedDeliveryAgent(e.target.value)}
                required
              >
                <MenuItem value="">
                  <em>Select Delivery Agent</em>
                </MenuItem>
                {deliveryAgents
                  .filter(agent => agent.isActive)
                  .map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{agent.name}</Typography>
                        <Chip 
                          label={agent.status} 
                          size="small" 
                          color={
                            agent.status === DeliveryPersonStatus.AVAILABLE ? 'success' : 
                            agent.status === DeliveryPersonStatus.BUSY ? 'warning' : 'default'
                          }
                          sx={{ ml: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ({agent.vehicleType})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
              {(selectedOrder?.assignedDeliveryAgent || selectedOrder?.deliveryAgent) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Currently assigned to: {getOrderDeliveryAgent(selectedOrder)?.name || 'Unknown Agent'}
                </Typography>
              )}
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={statusNotes}
            onChange={(e) => setStatusNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Orders;
